import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  DocumentTextIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
  SparklesIcon,
  UsersIcon,
  FolderIcon,
  QueueListIcon,
  RectangleStackIcon,
  BookOpenIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  MicrophoneIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { BunnyIcon } from '../icons/BunnyIcon';
import { Rueckfrage } from '../ui/Rueckfrage';
import { useAuth } from '../../contexts/authContextValue';
import { useRueckfrage } from '../../hooks/useRueckfrage';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
// ⚠️ `types/index` und NICHT `types/user`: dort lag ein ZWEITES `UserRole`-Enum, dem
// `CONTENT_CREATOR` fehlte — ausgerechnet in der Datei, die den Menü-Rollenfilter speist.
// Neunte Fundstelle desselben Kopier-Musters in dieser Schiene.
import { UserRole } from '../../types';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  roles?: UserRole[]; // Optionale Rollen, die diesen Eintrag sehen können
}

// Ein Nav-Eintrag ist entweder ein Standalone-Item oder eine Sektion mit Label + Items.
type NavEntry = NavItem | { section: string; items: NavItem[] };

const isSection = (entry: NavEntry): entry is { section: string; items: NavItem[] } =>
  'section' in entry;

const getNavigation = (t: TFunction, userRole?: UserRole): NavEntry[] => {
  // Gruppierte Struktur (Stil: Supabase Studio / Linear) mit Sektions-Labels.
  const allEntries: NavEntry[] = [
    // Dashboard — ganz oben, kein Sektions-Label
    { name: t('nav.dashboard'), href: '/', icon: HomeIcon },
    // — INHALTE —
    {
      section: 'Inhalte',
      items: [
        { name: 'Audio', href: '/audio', icon: MusicalNoteIcon, roles: [UserRole.ADMIN, UserRole.MODERATOR, UserRole.CONTENT_CREATOR] },
        { name: 'Video', href: '/video', icon: VideoCameraIcon, roles: [UserRole.ADMIN, UserRole.MODERATOR, UserRole.CONTENT_CREATOR] },
        { name: 'Playlists', href: '/playlists', icon: QueueListIcon, roles: [UserRole.ADMIN] },
        { name: 'Alben', href: '/albums', icon: RectangleStackIcon, roles: [UserRole.ADMIN] },
        { name: 'Interpreten', href: '/artists', icon: MicrophoneIcon, roles: [UserRole.ADMIN] },
        { name: 'MindBooks', href: '/mindbooks', icon: BookOpenIcon, roles: [UserRole.ADMIN, UserRole.MODERATOR, UserRole.CONTENT_CREATOR] },
        { name: 'Themen-Kategorien', href: '/topic-categories', icon: FolderIcon },
      ],
    },
    // Standalone-Items (kein Sektions-Label)
    { name: 'Mediathek', href: '/bunny-cdn', icon: BunnyIcon, roles: [UserRole.ADMIN] },
    { name: 'Affirmationen', href: '/content/affirmations', icon: SparklesIcon },
    { name: t('nav.blog'), href: '/content/blog', icon: DocumentTextIcon },
    // ⚠️ Hier standen „Analytics" und — in einer Sektion „Einstellungen" neben Benutzer —
    // „Einstellungen". Beide führten auf ein leeres `<div>`, und beiden fehlt auch das Backend;
    // sie sind samt Route entfernt (App.tsx). Übrig bleibt Benutzer, und ein einzelner Eintrag
    // trägt keine Sektionsüberschrift — schon gar nicht die des gelöschten Nachbarn.
    // Die i18n-Schlüssel `nav.analytics`/`nav.settings` bleiben bestehen: die Sprachdatei wird
    // vollständig gehalten (Tom-Entscheidung 2026-08-12).
    { name: t('nav.users'), href: '/users', icon: UsersIcon, roles: [UserRole.ADMIN] },
  ];

  // ⚠️ Audio/Video/MindBooks tragen `CONTENT_CREATOR`, weil das Backend die Rolle dort
  // ausdrücklich zulässt (audioRoutes/videoRoutes/mindBookRoutes: authorize([...,'CONTENT_CREATOR'])).
  // Vorher war das Menü enger als das Backend — nicht als Entscheidung, sondern weil das hier
  // importierte Enum die Rolle gar nicht kannte. Playlists/Alben/Interpreten/Mediathek/Benutzer
  // bleiben ADMIN-only: dort lässt das Backend `CONTENT_CREATOR` auch nicht durch.
  // Rollen-Filter — Verhalten unverändert beibehalten:
  // Admin/Moderator sehen alles; ohne Rolle nur rollenlose Items; sonst per item.roles.
  const userRoleStr = userRole ? String(userRole).toUpperCase() : undefined;

  const itemVisible = (item: NavItem): boolean => {
    // Admin/Moderator sehen alle Items (bisheriges Debug-Verhalten beibehalten)
    if (userRoleStr === 'ADMIN' || userRoleStr === 'MODERATOR') {
      return true;
    }
    // Keine Rollen definiert → für alle sichtbar
    if (!item.roles || item.roles.length === 0) {
      return true;
    }
    // Ohne erkannte Rolle nur rollenlose Items
    if (!userRoleStr) {
      return false;
    }
    // Sonst: prüfe, ob die Benutzerrolle enthalten ist
    return item.roles.some(role => String(role).toUpperCase() === userRoleStr);
  };

  // Items filtern; Sektion ausblenden, wenn nach Filterung leer.
  return allEntries.reduce<NavEntry[]>((acc, entry) => {
    if (isSection(entry)) {
      const visibleItems = entry.items.filter(itemVisible);
      if (visibleItems.length > 0) {
        acc.push({ section: entry.section, items: visibleItems });
      }
    } else if (itemVisible(entry)) {
      acc.push(entry);
    }
    return acc;
  }, []);
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { rückfrage, frage, antworte } = useRueckfrage();

  // ⚠️ Der Sonderfall unter den Umstellungen: hier wird NICHT gelöscht, sondern abgemeldet.
  // Deshalb KEIN `gefahr: true` — der rote Knopf und die dringende Rolle `alertdialog` gehören den
  // Aktionen, die etwas unwiederbringlich wegnehmen. Wer sich versehentlich abmeldet, meldet sich
  // wieder an; nichts ist verloren außer dem Weg dorthin. Eine Kennzeichnung, die überall Gefahr
  // ruft, wird überall überhört — genau die Unterscheidung, die `RückfrageWunsch.gefahr`
  // begründet.
  //
  // ⚠️ Stapelordnung, gemessen statt angenommen — die Sidebar ist auf JEDER Seite montiert und
  // steht damit neben jedem denkbaren Overlay:
  //   • `Layout.tsx` hängt diese Komponente in ein `fixed inset-y-0 left-0 z-50 w-64 transform`.
  //     Das `transform` öffnet einen Stapelkontext UND wird zum Bezugsrahmen für `position: fixed`
  //     darin. Ein Dialog, der im Fluss dieser Datei stünde, läge damit in einer 256 px breiten
  //     Spalte statt über der Seite. `ModalSchicht` hängt ihn per Portal an `document.body`,
  //     außerhalb dieses Rahmens — deshalb greift das hier überhaupt.
  //   • Über dem Portal-Schleier (`z-[55]`) liegt in der ganzen Anwendung nur der schwebende
  //     Meldungs-Balken (`z-[60]`, gewollt). Alle Overlays liegen auf `z-50` (`.sidebar` selbst,
  //     der Layout-Rahmen, `.modal-overlay`, `.bunny-file-picker-overlay`,
  //     `.mediathek-dialog-schleier`, TrackPicker, ProductPicker, Themen-Formulare), das mobile
  //     Menü auf `z-40`.
  //   • Das eigene Aufklapp-Menü bleibt bewusst offen: sein Rücken-Schleier (`z-10`) und die Karte
  //     (`z-20`) liegen INNERHALB des `z-50`-Kontexts der Sidebar, kommen also nie über die
  //     Rückfrage. Sie sind während der Frage weder anklickbar noch per Tabulator erreichbar — der
  //     Fokusfang hält im Dialog. Nach „Abbrechen" steht der Fokus wieder auf „Abmelden".
  const handleLogout = async () => {
    if (
      !(await frage({
        titel: t('auth.confirmLogout') || 'Möchten Sie sich wirklich abmelden?',
        text: 'Die Sitzung wird beendet und Sie landen auf der Anmeldeseite. Nicht gespeicherte Eingaben in einem offenen Formular gehen dabei verloren.',
        // Ohne Angabe stünde hier „Weiter" — bei einer Ja/Nein-Frage sagt die Beschriftung besser,
        // was gleich passiert. Derselbe Schlüssel wie am auslösenden Menüpunkt.
        bestätigenText: t('auth.logout') || 'Abmelden',
      }))
    ) {
      return;
    }
    try {
      console.log('🚪 Logging out from sidebar...');
      await logout();
      console.log('✅ Logout successful');
      window.location.href = '/login';
    } catch (error) {
      console.error('❌ Logout failed:', error);
    }
  };

  // Konvertiere String-Rolle zu UserRole Enum falls nötig
  const userRole = user?.role
    ? (typeof user.role === 'string'
        ? (user.role as UserRole)
        : user.role as UserRole)
    : undefined;

  // Filtere Navigation basierend auf Benutzerrolle
  const navigation = getNavigation(t, userRole);

  // Rendert einen einzelnen Nav-Eintrag (aktiv-Logik via location.pathname)
  const renderItem = (item: NavItem) => {
    const isActive = location.pathname === item.href ||
      (item.href !== '/' && location.pathname.startsWith(item.href));

    return (
      <Link
        key={item.href}
        to={item.href}
        className={`nav-item ${isActive ? 'active' : ''}`}
      >
        <item.icon className="w-5 h-5 mr-3" />
        <span className="flex-1">{item.name}</span>
        {item.badge && (
          <span className="badge badge-primary text-xs">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <img
              src="/images/EE_Signet_Gradient.png"
              alt="Energetic Eternity Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 className="text-lg font-bold text-secondary-900">Energetic Eternity</h1>
            <p className="text-xs text-secondary-500">CMS</p>
          </div>
        </div>
      </div>

      <div className="sidebar-content">
        <nav className="space-y-1">
          {navigation.map((entry) =>
            isSection(entry) ? (
              <div key={entry.section}>
                <p className="px-6 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-secondary-400">
                  {entry.section}
                </p>
                {entry.items.map(renderItem)}
              </div>
            ) : (
              renderItem(entry)
            )
          )}
        </nav>
      </div>

      <div className="sidebar-footer">
        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="w-full flex items-center justify-between space-x-2 hover:bg-secondary-100 rounded-lg p-1 transition-colors"
          >
            <div className="flex items-center space-x-2 min-w-0">
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer">
                <span className="text-sm font-medium text-primary-700">
                  {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                </span>
              </div>
              <div className="text-left min-w-0">
                <p className="text-sm font-medium text-secondary-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-secondary-500 capitalize truncate">
                  {user?.role?.toLowerCase()}
                </p>
              </div>
            </div>
            <ChevronUpIcon
              className={`w-4 h-4 text-secondary-500 flex-shrink-0 transition-transform ${isUserMenuOpen ? '' : 'rotate-180'}`}
            />
          </button>

          {/* Dropdown Menu — öffnet nach OBEN (am unteren Rand der Sidebar) */}
          {isUserMenuOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsUserMenuOpen(false)}
              />
              {/* Menu */}
              <div className="absolute bottom-full mb-2 left-0 right-0 bg-white rounded-lg shadow-lg z-20 border border-secondary-200">
                <div className="py-1">
                  <div className="px-4 py-3 border-b border-secondary-200">
                    <p className="text-sm font-medium text-secondary-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-secondary-500 truncate">
                      {user?.email}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-3 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors"
                  >
                    <UserIcon className="w-4 h-4 mr-2" />
                    Profil
                  </button>
                  <button
                    onClick={() => void handleLogout()}
                    className="w-full flex items-center px-4 py-3 text-sm text-secondary-700 hover:bg-secondary-50 transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                    {t('auth.logout') || 'Abmelden'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Steht im Fluss dieser Datei, landet aber per Portal an `document.body` — siehe die
          Messung am Abmelde-Pfad oben. Ohne das Portal läge der Schleier im 256 px breiten,
          transformierten Rahmen aus `Layout.tsx`. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />
    </div>
  );
};

export default Sidebar;
